import { observer } from 'mobx-react-lite';
import * as React from 'react';
import SxSView, { SxSViewProps } from './SxSView';
import { AnimationClassNames } from '@uifabric/styling/lib/classNames/AnimationClassNames';
import { FocusTrapZone } from 'office-ui-fabric-react/lib/FocusTrapZone';
import { Layer } from 'office-ui-fabric-react/lib/Layer';
import { Modal } from 'office-ui-fabric-react/lib/Modal';

export interface SxSLayerViewProps extends SxSViewProps {
    hideOwaHeader?: boolean;
    modal?: boolean;
    newStyle?: boolean;
    onAnimationEnd?: () => void;
    shouldHide?: boolean;
}

const styles = require('./SxSView.scss');
const classNames = require('classnames/bind').bind(styles);
const animationEndEventNames = [
    'webkitAnimationEnd',
    'mozAnimationEnd',
    'MSAnimationEnd',
    'oAnimationEnd',
    'animationend',
];

export default observer(function SxSLayerView(props: SxSLayerViewProps) {
    React.useEffect(() => {
        if (shouldDoExcelWorkAroundForEdge()) {
            const app = document.getElementById('app');
            if (app) {
                app.className = styles.excelWorkAroundForEdge;
            }
        }
        return () => {
            removeAnimationCallback();
            if (shouldDoExcelWorkAroundForEdge()) {
                const app = document.getElementById('app');
                if (app) {
                    app.className = '';
                }
            }
        };
    }, []);
    const currentModalRootElement = React.useRef<Element>(null);
    const wasShown = React.useRef(false);
    const onModalComponentChanged = (div: HTMLDivElement) => {
        removeAnimationCallback();
        if (div) {
            let element = div.parentElement;
            while (element && !element.classList.contains(styles.owaSxSDialogRoot)) {
                element = element.parentElement;
            }
            currentModalRootElement.current = element;
            animationEndEventNames.forEach(eventName => {
                element.addEventListener(eventName, onModalAnimationEnd);
            });
        }
    };
    const removeAnimationCallback = () => {
        if (currentModalRootElement.current) {
            const element = currentModalRootElement.current;
            currentModalRootElement.current = null;
            animationEndEventNames.forEach(eventName => {
                element.removeEventListener(eventName, onModalAnimationEnd);
            });
        }
    };
    const onModalAnimationEnd = (evt: AnimationEvent) => {
        if (evt.target === currentModalRootElement.current && props.onAnimationEnd) {
            props.onAnimationEnd();
        }
    };
    const onModalDismiss = (ev?: React.MouseEvent<HTMLButtonElement>) => {
        props.onClose();
    };
    const hideOwaHeader =
        props.modal || props.hideOwaHeader === undefined ? false : props.hideOwaHeader;
    const className = classNames(
        props.className,
        props.modal ? null : styles.owaSxSLayer,
        hideOwaHeader ? null : styles.showOwaHeader,
        props.shouldHide && !props.modal ? styles.owaSxSLayerHidden : null
    );
    wasShown.current = wasShown.current || !props.shouldHide;
    const sxsView = props.newStyle ? (
        <SxSView
            {...{
                ...props,
                className,
                useFullWidthCommandBar: true,
                neverUseHighContrastCommandBar: true,
                slidingPanelEffect: true,
            }}
        />
    ) : (
        <SxSView {...{ ...props, className }} />
    );
    const modalRootClassName = classNames(
        styles.owaSxSDialogRoot,
        props.shouldHide
            ? wasShown.current
                ? AnimationClassNames.fadeOut400
                : null
            : AnimationClassNames.fadeIn400
    );
    return props.modal ? (
        <Modal
            containerClassName={styles.owaSxSDialog}
            className={modalRootClassName}
            isOpen={true}
            onDismiss={onModalDismiss}>
            <div className={styles.owaSxSDialogSpacer} ref={onModalComponentChanged} />
            {sxsView}
        </Modal>
    ) : (
        <Layer>
            <FocusTrapZone>{sxsView}</FocusTrapZone>
        </Layer>
    );
});

function shouldDoExcelWorkAroundForEdge(): boolean {
    const ua = window?.navigator?.userAgent ? window.navigator.userAgent : '';
    const isBrowserIE =
        ua.indexOf('MSIE') !== -1 || // pre-IE11
        (ua.indexOf('rv:') !== -1 && ua.indexOf('Trident') !== -1); // IE11+
    const isBrowserEdge = ua.indexOf('Edge') !== -1;
    return isBrowserIE || isBrowserEdge;
}
